import React, { useState, useEffect } from 'react';
import { Scale, Lock, Check, Zap, Building2, Star, LogOut, AlertCircle, Users, HardDrive } from 'lucide-react';
import { listFirmUsers } from '../lib/db';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { PLANS as PLAN_META, ANNUAL_DISCOUNT_PERCENT, FOUNDING_MEMBER_LOCK_MONTHS } from '../lib/plans';

const PAYSTACK_PUBLIC_KEY  = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY  || '';

// Monthly plan codes (fallback to the legacy *_PLAN_CODE names so any
// deployment still on the v1.0.18-era env-var schema keeps working).
const PAYSTACK_BASIC_MONTHLY_PLAN =
  process.env.REACT_APP_PAYSTACK_BASIC_MONTHLY_PLAN_CODE
  || process.env.REACT_APP_PAYSTACK_BASIC_PLAN_CODE
  || '';
const PAYSTACK_PRO_MONTHLY_PLAN =
  process.env.REACT_APP_PAYSTACK_PRO_MONTHLY_PLAN_CODE
  || process.env.REACT_APP_PAYSTACK_PRO_PLAN_CODE
  || '';

// Annual plan codes (set when the firm picks the annual toggle).
const PAYSTACK_BASIC_ANNUAL_PLAN = process.env.REACT_APP_PAYSTACK_BASIC_ANNUAL_PLAN_CODE || '';
const PAYSTACK_PRO_ANNUAL_PLAN   = process.env.REACT_APP_PAYSTACK_PRO_ANNUAL_PLAN_CODE   || '';

// Pricing is FLAT per-firm/month (not per seat). Each plan includes a
// fixed seat cap — Basic up to 5, Pro up to 15 — that the database
// trigger in migration 141 enforces server-side. A firm exceeding their
// cap needs to upgrade tier, not pay extra per seat.
//
// Why flat instead of per-seat: Paystack inline.js doesn't natively
// multiply plan amount by quantity. Passing { plan: PLN_xxx, amount: N }
// causes Paystack to ignore the amount and bill the plan's amount only,
// so multi-seat per-user pricing was silently broken (firms paid 1× when
// the page showed N×). Flat pricing eliminates the mismatch.
//
// Source of truth: src/lib/plans.js — also used by UpgradeModal and the
// firm onboarding wizard.
const PRICE_MONTHLY = {
  basic: PLAN_META.basic.priceGhs,        // GH₵200/firm/mo
  pro:   PLAN_META.pro.priceGhs,          // GH₵400/firm/mo
};

const PRICE_ANNUAL = {
  basic: PLAN_META.basic.priceGhsAnnual,  // GH₵1,920/firm/yr
  pro:   PLAN_META.pro.priceGhsAnnual,    // GH₵3,840/firm/yr
};

// Seat caps per plan — display only; the migration-141 trigger is the
// authoritative gate. We mirror these from the webhook's PLAN_CAPACITY
// (supabase/functions/paystack-webhook/index.ts) so the page matches
// the cap that actually gets applied on subscription.create.
const SEAT_CAPS = {
  basic: 5,
  pro:   10,
  // enterprise: null = unlimited (firms > 10 lawyers, custom contract)
};

// Display config (icons, badges, feature bullets). Prices + storage caps
// flow from PLAN_META — storage no longer duplicated as a feature bullet,
// it's a first-class badge under the price.
const PLANS = [
  {
    value:       'basic',
    label:       'Basic',
    icon:        Star,
    badge:       '',
    color:       'border-slate-200 dark:border-slate-700',
    seatLabel:   'Up to 5 seats',
    storageGb:   PLAN_META.basic.storageGb,   // 20 GB
    features: [
      'Unlimited matters & case files',
      'Client & contact management',
      'Tasks, calendar & deadlines',
      'Time tracking & invoicing',
      'Expense tracking',
      'Dashboard & activity feed',
      'Add-on storage available',
    ],
  },
  {
    value:       'pro',
    label:       'Pro',
    icon:        Zap,
    badge:       'Most popular',
    color:       'border-indigo-500',
    seatLabel:   'Up to 10 seats',
    storageGb:   PLAN_META.pro.storageGb,     // 200 GB
    features: [
      'Everything in Basic',
      'Client portal',
      'Trust / client account management',
      'Advanced reports & analytics',
      'Role-based access control',
      'Conflict of interest checker',
      'Online intake forms',
      'Employee & HR management',
      'Add-on storage available',
    ],
  },
  {
    value:       'enterprise',
    label:       'Enterprise',
    icon:        Building2,
    badge:       '',
    color:       'border-slate-400 dark:border-slate-500',
    seatLabel:   'Unlimited seats',
    storageGb:   null,                         // unlimited
    features: [
      'Everything in Pro',
      'AI case search & document review',
      'Smart deadline alerts (AI)',
      'Dedicated account manager',
      'Custom integrations & API access',
    ],
  },
];

// Load Paystack's inline.js script once
function usePaystackScript() {
  const [ready, setReady] = useState(!!window.PaystackPop);
  useEffect(() => {
    if (window.PaystackPop) { setReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setReady(true);
    document.body.appendChild(script);
    return () => { /* leave script in DOM for reuse */ };
  }, []);
  return ready;
}

export default function SubscriptionSuspendedPage() {
  const { firm, firmId, refresh } = useFirm();
  const { user, logout } = useAuth();
  const paystackReady = usePaystackScript();

  const [selected, setSelected]         = useState('pro');
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' | 'annual'
  const [userCount, setUserCount]       = useState(null);       // null = unknown / still loading
  const [saving, setSaving]             = useState(false);
  const [done, setDone]                 = useState(false);
  const [error, setError]               = useState('');

  // Fetch active user count once so we can quote the total monthly bill.
  // RLS scopes the result to this firm automatically.
  useEffect(() => {
    if (!firmId) return;
    listFirmUsers(firmId).then(({ data }) => {
      const active = (data || []).filter((u) => (u.status || 'Active') !== 'inactive');
      setUserCount(Math.max(1, active.length || 1));
    }).catch(() => setUserCount(1));
  }, [firmId]);

  // Flat per-firm pricing — see the PRICE_MONTHLY / PRICE_ANNUAL
  // declarations at the top of the file for the rationale (Paystack
  // inline.js limitation). billingTotal is just the plan price; the
  // seat cap is enforced server-side by migration 141.
  const isAnnual     = billingPeriod === 'annual';
  const planPrice    = (isAnnual ? PRICE_ANNUAL[selected] : PRICE_MONTHLY[selected]) || 0;
  const billingTotal = planPrice;
  const billingTotalPesewas = billingTotal * 100;

  // Capacity-check: does the current user count fit the selected plan's
  // seat cap? Drives the upgrade-needed warning under the price block.
  const seatCap        = SEAT_CAPS[selected] ?? null;
  const exceedsSeatCap = seatCap !== null && userCount !== null && userCount > seatCap;

  // Helper: what's the "save X" amount on annual vs paying 12× monthly?
  // Used by the toggle UI to show the discount inline.
  const annualSavings = (planKey) => {
    const m = PRICE_MONTHLY[planKey] || 0;
    const a = PRICE_ANNUAL[planKey]  || 0;
    if (!m || !a) return 0;
    return Math.max(0, (m * 12) - a);
  };

  // ── Pay via Paystack ─────────────────────────────────────────────────────
  const paySelected = () => {
    if (!paystackReady) { setError('Payment system is still loading. Please try again.'); return; }
    if (!PAYSTACK_PUBLIC_KEY) {
      setError('Paystack public key is not configured. Add REACT_APP_PAYSTACK_PUBLIC_KEY to your .env.local file.');
      return;
    }
    if (!planPrice) {
      setError('Unsupported plan.');
      return;
    }
    // Block payment if the firm already has more users than the
    // selected plan allows — they'd pay and then immediately get
    // blocked by the seat-enforcement trigger when trying to use
    // the system. Force them to pick the right tier first.
    if (exceedsSeatCap) {
      setError(
        `Your firm has ${userCount} active members but ${selected.charAt(0).toUpperCase() + selected.slice(1)} `
        + `is limited to ${seatCap} seats. Pick a higher tier or remove members first.`
      );
      return;
    }

    setError('');
    setSaving(true);

    // Pick the right Paystack plan code for the (plan × billing period)
    // combination. Falls back to a one-off charge of the computed total
    // when no plan code is configured for that combination.
    const planCode =
      selected === 'pro' && isAnnual  ? PAYSTACK_PRO_ANNUAL_PLAN   :
      selected === 'pro'              ? PAYSTACK_PRO_MONTHLY_PLAN  :
      isAnnual                        ? PAYSTACK_BASIC_ANNUAL_PLAN :
                                        PAYSTACK_BASIC_MONTHLY_PLAN;

    const handler = window.PaystackPop.setup({
      key:      PAYSTACK_PUBLIC_KEY,
      email:    user?.email || firm?.email || '',
      currency: 'GHS',
      // ⚠ Paystack inline.js: if BOTH `amount` and `plan` are passed,
      // Paystack treats the transaction as a one-off charge and never
      // creates a subscription (no subscription.create event fires →
      // webhook can't activate the firm). Pass `plan` exclusively when
      // we have a plan code; let Paystack derive the amount from the
      // plan record. Only fall back to `amount` for the no-plan
      // scenario (e.g. before plans are configured in Paystack).
      ...(planCode
        ? { plan: planCode }
        : { amount: billingTotalPesewas }
      ),
      label:    firm?.name || 'Law Firm',
      metadata: {
        firm_id:        firmId,
        firm_name:      firm?.name || '',
        plan:           selected,
        billing_period: billingPeriod,   // FYI only — webhook reads from plan.interval
        user_count:     userCount,
      },
      channels: ['card', 'mobile_money', 'bank'],

      // ⚠ Paystack's inline.js validates `callback` with a strict
      // typeof+constructor check that REJECTS async arrow functions
      // with "Attribute callback must be a valid function". Keep this
      // a plain sync function and run the async polling inside an
      // immediately-invoked async wrapper. Paystack doesn't await the
      // callback's return value anyway — it's fire-and-forget from
      // its perspective.
      callback: (response) => {
        // ── Wait for the Paystack webhook to activate the firm ──
        //
        // We intentionally do NOT write subscription_plan /
        // subscription_status from the client any more. Migration 155
        // blocks user-side writes to those columns (was a security
        // bypass — see CHANGELOG v1.0.16). The webhook (service-role)
        // is now the single source of truth for plan activation; it
        // also sets seat_limit / storage_limit_mb from PLAN_CAPACITY
        // in lockstep with the plan, which the client can't do
        // safely from a callback anyway.
        //
        // Paystack's `callback` fires the moment the inline.js sees a
        // successful charge, which is usually 0.5–2 seconds before
        // the webhook lands. Poll the firm row (via refresh()) until
        // subscription_status flips to 'active' or we hit the
        // timeout. The webhook is fast (sub-second under load) but
        // the network round-trip plus PostgREST schema cache means
        // 1–5 seconds is realistic.

        (async () => {
          const POLL_INTERVAL_MS = 1500;
          const POLL_TIMEOUT_MS  = 25_000;   // ~16 polls. Generous: covers webhook lag + retries.
          const startedAt        = Date.now();

          const isActive = () =>
            (firm?.subscription_status === 'active') &&
            (firm?.subscription_plan === selected || firm?.subscription_plan === 'enterprise');

          try {
            // Loop: refresh() pulls a fresh firm row through FirmContext.
            // We stop as soon as the webhook-applied state is visible to
            // the client, or when we hit the timeout.
            // eslint-disable-next-line no-constant-condition
            while (true) {
              await refresh();
              if (isActive()) {
                setDone(true);
                break;
              }
              if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                // Payment succeeded (Paystack confirmed). The webhook
                // either hasn't landed yet or failed. We can't tell from
                // here. Surface a friendly message with the reference so
                // ops can confirm in paystack_webhook_events.
                setError(
                  'Payment confirmed — your reference is ' + response.reference + '. '
                  + 'Activation is taking longer than usual; please refresh in a minute. '
                  + 'If it still says suspended, email support with that reference.'
                );
                break;
              }
              await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            }
          } finally {
            setSaving(false);
          }
        })();
      },
      onClose: () => setSaving(false),
    });

    handler.openIframe();
  };

  const contactSales = () => {
    const subject = encodeURIComponent('Enterprise Plan Inquiry — ' + (firm?.name || ''));
    window.open(`mailto:sales@docketflowapp.com?subject=${subject}`, '_blank');
  };

  const handleActivate = () => {
    if (selected === 'enterprise') { contactSales(); return; }
    paySelected();
  };

  // ── Success ──────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
            <Check size={32} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Welcome back!</h2>
          <p className="text-slate-500 dark:text-slate-400">Your account has been reactivated. Redirecting…</p>
        </div>
      </div>
    );
  }

  const ctaLabel = () => {
    if (saving) return 'Processing…';
    if (selected === 'enterprise') return 'Contact sales';
    if (!paystackReady) return 'Loading payment…';
    if (exceedsSeatCap) return `${selected.charAt(0).toUpperCase() + selected.slice(1)} is limited to ${seatCap} seats`;
    const cadence = isAnnual ? 'year' : 'month';
    return `Pay GH₵${billingTotal.toLocaleString()} / ${cadence} with Paystack`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">

        {/* Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex w-12 h-12 rounded-2xl bg-indigo-600 items-center justify-center mb-4 shadow-lg">
            <Scale size={22} className="text-white" />
          </div>
          <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
            <Lock size={12} />
            Trial expired — account suspended
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Choose a plan to continue</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">
            Your 14-day free trial has ended. Pick a plan below to restore full access. Your data is intact.
          </p>
        </div>

        {/* Founding-member pricing banner — sets the expectation that
           these prices are time-limited and annual signups lock the
           rate. Drives annual conversions which is what we want for
           cash flow + LTV during launch. */}
        <div className="mb-5 flex justify-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs">
            <Star size={12} className="flex-shrink-0" />
            <span>
              <strong>Founding-member pricing.</strong>{' '}
              Annual signups lock these rates for {FOUNDING_MEMBER_LOCK_MONTHS} months.
            </span>
          </div>
        </div>

        {/* Billing period toggle — monthly vs annual */}
        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex items-center bg-slate-100 dark:bg-slate-800 rounded-full p-1 text-sm">
            <button
              type="button"
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-1.5 rounded-full transition ${
                billingPeriod === 'monthly'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm font-semibold'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('annual')}
              className={`px-4 py-1.5 rounded-full transition flex items-center gap-2 ${
                billingPeriod === 'annual'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm font-semibold'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              Annual
              <span className="inline-flex items-center text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-full">
                Save {ANNUAL_DISCOUNT_PERCENT}%
              </span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const isSelected = selected === plan.value;
            const isEnterprise = plan.value === 'enterprise';
            const handleClick = () => {
              if (isEnterprise) {
                contactSales();
                return;
              }
              setSelected(plan.value);
            };
            return (
              <button
                key={plan.value}
                type="button"
                onClick={handleClick}
                className={`relative text-left p-5 rounded-xl border-2 transition bg-white dark:bg-slate-900 focus:outline-none
                  ${isSelected
                    ? 'border-indigo-500 ring-2 ring-indigo-500/20'
                    : `${plan.color} hover:border-indigo-300 dark:hover:border-indigo-700`
                  }`}
              >
                {plan.badge && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-indigo-600 text-white whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                    <Check size={11} className="text-white" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-indigo-600' : 'bg-slate-100 dark:bg-slate-700'}`}>
                    <Icon size={15} className={isSelected ? 'text-white' : 'text-slate-500 dark:text-slate-300'} />
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white">{plan.label}</span>
                </div>
                {/* Price block — CaseFlow-style. Annual shows the
                   effective monthly price (annual ÷ 12) so customers
                   compare apples-to-apples against monthly. Monthly
                   price is struck through on annual cards. */}
                <div className="mb-3">
                  {isEnterprise ? (
                    <>
                      <span className="text-2xl font-bold text-slate-900 dark:text-white">Custom</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">pricing</span>
                    </>
                  ) : isAnnual ? (
                    <>
                      {/* Struck-through monthly above the discounted annual eff/mo */}
                      <div className="text-xs text-slate-400 dark:text-slate-500 line-through leading-none">
                        GH₵{PRICE_MONTHLY[plan.value]?.toLocaleString() ?? '—'}
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-slate-900 dark:text-white">
                          GH₵{Math.round((PRICE_ANNUAL[plan.value] || 0) / 12).toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">per firm / month</span>
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Billed annually — GH₵{PRICE_ANNUAL[plan.value]?.toLocaleString()}/yr
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-slate-900 dark:text-white">
                          GH₵{PRICE_MONTHLY[plan.value]?.toLocaleString() ?? '—'}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">per firm / month</span>
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Billed monthly
                      </div>
                    </>
                  )}
                </div>

                {/* Seats + storage badges — first-class, not buried in features */}
                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                    <Users size={10} />
                    {plan.seatLabel}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                    <HardDrive size={10} />
                    {plan.storageGb == null ? 'Unlimited storage' : `${plan.storageGb} GB storage`}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                      <Check size={11} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isEnterprise && (
                  <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                      Contact us →
                    </span>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                      Custom pricing for larger firms and chambers. We'll set up your plan with you.
                    </p>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Seat-utilization + flat-price explainer */}
        {selected !== 'enterprise' && userCount !== null && seatCap !== null && (
          <div className={`flex items-center gap-2 justify-center text-xs mb-3 ${
            exceedsSeatCap
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-slate-500 dark:text-slate-400'
          }`}>
            <Users size={12} />
            <span>
              Your firm: <strong>{userCount}</strong> active member{userCount === 1 ? '' : 's'} of{' '}
              <strong>{seatCap}</strong> seat{seatCap === 1 ? '' : 's'} included
              {exceedsSeatCap && (
                <> — exceeds the {selected.charAt(0).toUpperCase() + selected.slice(1)} cap. Pick a higher tier.</>
              )}
              {!exceedsSeatCap && (
                <>. Flat <strong className="text-slate-700 dark:text-slate-200">GH₵{billingTotal.toLocaleString()}/{isAnnual ? 'yr' : 'mo'}</strong> for the whole firm.</>
              )}
            </span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-xl px-4 py-3 mb-4">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-6 py-4">
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center sm:text-left">
            {selected === 'enterprise'
              ? 'Our team will contact you within 1 business day to set up your plan.'
              : `Billed ${isAnnual ? 'annually' : 'monthly'} via Paystack. Cards, MTN MoMo, AirtelTigo, Vodafone Cash accepted. Cancel anytime from Settings.`}
          </p>
          <button
            onClick={handleActivate}
            disabled={saving || (selected !== 'enterprise' && (!paystackReady || exceedsSeatCap))}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold transition"
          >
            {ctaLabel()}
          </button>
        </div>

        {/* Sign out */}
        <div className="text-center mt-6">
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition mx-auto"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>

      </div>
    </div>
  );
}
